import { IAccurateEstimatedOfferResponse } from '@services/getAccurateEstimatedOffer';
import { IPostSelectedLoanResponse } from '@services/lenderInformation';
import { IPostPlateToVINResponse } from '@services/postPlateToVIN';
import { ApiResources } from '@services/utils';
import { Screens } from '@shared/constants/screens';
import { ILicensePlateInfoForm } from 'types/form';
import { IProveChallengeResponse, IProveCompleteResponse, IProveValidateResponse } from 'types/proveIdentity';

export interface LicensePlateData {
  estimatedMileageRange?: string;
  apiResponse?: {
    isSuccess?: boolean;
    message?: string;
    year?: number | string;
  } | null;
}

interface IManualLicensePlateInfoData {
  manualLicenseForm: {
    estimatedMileage: string;
    year: string | number;
  }
}

export interface MobileFlowData {
  isMobile?: boolean;
}

export interface Transition {
  //  eslint-disable-next-line @typescript-eslint/no-explicit-any
  when?: (data: any) => boolean;
  target: Screens;
}

export interface ScreenConfig {
  onLoad: Transition[];
  preSubmitOutcome: Transition[];
  postSubmitOutcome: Transition[];
}

type ScreenGroup = Partial<Record<Screens, ScreenConfig>>;

export const PLATE_TO_VIN = {
  [Screens.ENTER_LICENSE_PLATE]: {
    onLoad: [
      {
        when: ({ estimatedMileageRange }: ILicensePlateInfoForm) =>
          estimatedMileageRange === 'Over 150,000 miles',
        target: Screens.DISQUALIFIED,
      },
      {
        when: ({ vin }: IPostPlateToVINResponse) => !!vin,
        target: Screens.VERIFY_VEHICLE_INFO,
      },
      {
        when: ({ vin, year, make, model }: IPostPlateToVINResponse) =>
           !vin && !!(year || make || model),
        target: Screens.ENTER_LICENSE_PLATE, // if year, make, model is present from source even then we need VIN
      },
    ],
    preSubmitOutcome: [
      {
        when: ({ estimatedMileageRange }: LicensePlateData) =>
          estimatedMileageRange === 'Over 150,000 miles',
        target: Screens.DISQUALIFIED,
      },
    ],
    postSubmitOutcome: [
      {
        when: ({ apiResponse }: LicensePlateData) =>
          (!apiResponse?.isSuccess && apiResponse?.message === 'Not Found') ||
          apiResponse?.message === 'No plate match',
        target: Screens.VEHICLE_MANUAL_ENTER,
      },
      {
        when: ({ apiResponse }: LicensePlateData) =>
          Number(apiResponse?.year) < new Date().getFullYear() - 10,
        target: Screens.DISQUALIFIED,
      },
      {
        target: Screens.VERIFY_VEHICLE_INFO,
      },
    ],
  },

  [Screens.ENTER_LICENSE_PLATE_MANUALLY]: {
    onLoad: [],
    preSubmitOutcome: [
      
      {
        when: ({ manualLicenseForm }: IManualLicensePlateInfoData) =>
          manualLicenseForm.estimatedMileage === 'Over 150,000 miles',
        target: Screens.DISQUALIFIED,
      },
      {
        when: ({ manualLicenseForm }: IManualLicensePlateInfoData) =>
          Number(manualLicenseForm.year) < new Date().getFullYear() - 10,
        target: Screens.DISQUALIFIED,
      },
    ],
    postSubmitOutcome: [
      {
        when: ({ isMobile }: MobileFlowData) => isMobile === true,
        target: Screens.VERIFY_MOBILE_NUMBER,
      },
      {
        target: Screens.VERIFY_MOBILE_NUMBER_CHECK_YOUR_PHONE,
      },
    ],
  },

  [Screens.VEHICLE_MANUAL_ENTER]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ manualLicenseForm }: IManualLicensePlateInfoData) =>
          manualLicenseForm.estimatedMileage === 'Over 150,000 miles',
        target: Screens.DISQUALIFIED,
      },
      {
        when: ({ manualLicenseForm }: IManualLicensePlateInfoData) =>
          Number(manualLicenseForm.year) < new Date().getFullYear() - 10,
        target: Screens.DISQUALIFIED,
      },
      {
        target: Screens.VERIFY_VEHICLE_INFO,
      },
    ],
  },
};

export const PROVE = {
  [Screens.VERIFY_VEHICLE_INFO]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ isMobile }: MobileFlowData) => isMobile === true,
        target: Screens.VERIFY_MOBILE_NUMBER,
      },
      {
        target: Screens.VERIFY_MOBILE_NUMBER_CHECK_YOUR_PHONE,
      },
    ],
  },

  [Screens.VERIFY_MOBILE_NUMBER]: {
    onLoad: [
      {
        when: ({ authToken }: { authToken: string | null }) => !authToken,
        target: Screens.YOUR_INFORMATION_MANUALLY,
      },
    ],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ validateResponse }: { validateResponse: IProveValidateResponse }) =>
          !validateResponse.success,
        target: Screens.IDENTITY_FAILED,
      },
      {
        target: Screens.YOUR_INFO,
      },
    ],
  },

  [Screens.VERIFY_MOBILE_NUMBER_CHECK_YOUR_PHONE]: {
    onLoad: [],
    preSubmitOutcome: [
      {
        when: ({ authToken }: { authToken: string | null }) => !authToken,
        target: Screens.YOUR_INFORMATION_MANUALLY,
      },
    ],
    postSubmitOutcome: [
      {
        when: ({ validateResponse }: { validateResponse: IProveValidateResponse }) =>
          !validateResponse.success,
        target: Screens.IDENTITY_FAILED,
      },
      {
        target: Screens.YOUR_INFO,
      },
    ],
  },
};

export const CREATE_CONTACT = {
  [Screens.YOUR_INFO]: {
    onLoad: [
      {
        when: ({ nextEndpoint }: { nextEndpoint: string }) =>
          nextEndpoint === ApiResources.proveComplete,
        target: Screens.YOUR_INFORMATION_MANUALLY,
      },
    ],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ completeResponse }: { completeResponse?: IProveCompleteResponse }) =>
          completeResponse?.success === false,
        target: Screens.IDENTITY_FAILED,
      },
      {
        when: ({ challengeResponse }: { challengeResponse?: IProveChallengeResponse }) =>
          !!challengeResponse && !challengeResponse.individual,
        target: Screens.YOUR_INFORMATION_MANUALLY,
      },
      {
        when: ({ failedValidation }: { failedValidation: boolean }) => failedValidation,
        target: Screens.YOUR_INFORMATION_MANUALLY,
      },
      {
        target: Screens.SOFT_PULL_ACKNOWLEDGEMENT,
      },
    ],
  },

  [Screens.YOUR_INFORMATION_MANUALLY]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ completeResponse }: { completeResponse?: IProveCompleteResponse }) =>
          completeResponse?.success === false,
        target: Screens.IDENTITY_FAILED,
      },
      {
        target: Screens.SOFT_PULL_ACKNOWLEDGEMENT,
      },
    ],
  },
};

export const SOFT_PULL_LOAN = {
  [Screens.SOFT_PULL_ACKNOWLEDGEMENT]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ submitResponse }: { submitResponse?: IPostSelectedLoanResponse }) =>
          submitResponse !==undefined && !submitResponse,
        target: Screens.ENTER_LICENSE_PLATE,
      },
      {
        when: ({ softPullResponse }: { softPullResponse?: { offers: IAccurateEstimatedOfferResponse[]; usedDefaultScore: boolean } }) =>
          softPullResponse?.offers.length === 0 && softPullResponse?.usedDefaultScore,
        target: Screens.DISQUALIFIED_DECLINE_DEFAULT_CREDIT_SCORE,
      },
      {
        when: ({ softPullResponse }: { softPullResponse?: { offers: IAccurateEstimatedOfferResponse[]; usedDefaultScore: boolean } }) =>
          softPullResponse?.offers.length === 0 && !softPullResponse?.usedDefaultScore,
        target: Screens.DISQUALIFIED_DECLINE_LOW_CREDIT_SCORE,
      },
      {
        when: ({ loansCount }: { loansCount: number }) => loansCount === 0,
        target: Screens.LENDER_INFO,
      },
      {
        when: ({ matchingLoansCount }: { matchingLoansCount: number }) =>
          matchingLoansCount > 1,
        target: Screens.LOAN_SELECTION,
      },
      {
        target: Screens.OFFER_SELECTION,
      },
    ],
  },

  [Screens.LENDER_INFO]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ submitResponse }: { submitResponse?: IPostSelectedLoanResponse }) =>
          submitResponse !==undefined && !submitResponse,
        target: Screens.ENTER_LICENSE_PLATE,
      },
      {
        target: Screens.OFFER_SELECTION,
      },
    ],
  },

  [Screens.LOAN_SELECTION]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ submitResponse }: { submitResponse?: IPostSelectedLoanResponse }) =>
          submitResponse !==undefined && !submitResponse,
        target: Screens.ENTER_LICENSE_PLATE,
      },
      {
        when: ({ loansCount }: { loansCount: number }) => loansCount === 0,
        target: Screens.LENDER_INFO,
      },
      {
        target: Screens.OFFER_SELECTION,
      },
    ],
  },

  [Screens.OFFER_SELECTION]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ softPullResponse }: { softPullResponse: { offers: IAccurateEstimatedOfferResponse[]; usedDefaultScore: boolean } }) =>
          softPullResponse?.offers.length === 0 && softPullResponse?.usedDefaultScore,
        target: Screens.DISQUALIFIED_DECLINE_DEFAULT_CREDIT_SCORE,
      },
      {
        when: ({ softPullResponse }: { softPullResponse: { offers: IAccurateEstimatedOfferResponse[]; usedDefaultScore: boolean } }) =>
          softPullResponse?.offers.length === 0 && !softPullResponse?.usedDefaultScore,
        target: Screens.DISQUALIFIED_DECLINE_LOW_CREDIT_SCORE,
      },
      {
        target: Screens.CURRENT_ADDRESS_INFO,
      },
    ],
  },
};

export const ADDRESS = {
  [Screens.CURRENT_ADDRESS_INFO]: {
    onLoad: [
      {
        when: ({ addressFound }: { addressFound: boolean }) => !addressFound,
        target: Screens.CURRENT_ADDRESS_INFO_MANUALLY,
      },
    ],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ monthsAtAddress }: { monthsAtAddress: number }) => (monthsAtAddress ?? 0) < 24,
        target: Screens.TWO_YEARS_ADDRESS_HISTORY,
      },
      {
        target: Screens.EMPLOYMENT_AND_INCOME,
      },
    ],
  },

  [Screens.CURRENT_ADDRESS_INFO_MANUALLY]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        when: ({ monthsAtAddress }: { monthsAtAddress: number }) => (monthsAtAddress ?? 0) < 24,
        target: Screens.TWO_YEARS_ADDRESS_HISTORY,
      },
      {
        target: Screens.EMPLOYMENT_AND_INCOME,
      },
    ],
  },

  [Screens.TWO_YEARS_ADDRESS_HISTORY]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        target: Screens.EMPLOYMENT_AND_INCOME,
      },
    ],
  },
};

export const EMPLOYMENT = {
  [Screens.EMPLOYMENT_AND_INCOME]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        target: Screens.NEXT_STEPS,
      },
    ],
  },
};

export const NEXT_STEPS = {
  [Screens.NEXT_STEPS]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [
      {
        target: Screens.OFF_RAMP
      }
    ],
  },
};

export const OFF_RAMP = {
  [Screens.OFF_RAMP]: {
    onLoad: [],
    preSubmitOutcome: [],
    postSubmitOutcome: [],
  },
};

export type GroupName = 'PLATE_TO_VIN' | 'PROVE' | 'CREATE_CONTACT' | 'SOFT_PULL_LOAN' | 'ADDRESS' | 'EMPLOYMENT' | 'NEXT_STEPS' | 'OFF_RAMP';

export const screenGroupMap: Record<GroupName, ScreenGroup> = {
  PLATE_TO_VIN,
  PROVE,
  CREATE_CONTACT,
  SOFT_PULL_LOAN,
  ADDRESS,
  EMPLOYMENT,
  NEXT_STEPS,
  OFF_RAMP,
};
